/**
 * Pre-configured widget presets for the Exec dashboard.
 *
 * Each preset defines a complete widget config that answers a specific
 * leadership question. Organized by category matching the exec product vision.
 */
import type { GenericWidgetConfig } from './generic/types'
import { DIB_WIDGET_PRESETS } from './dib-widget-presets'

export interface WidgetPreset {
  /** Unique key for this preset */
  id: string
  /** Display title for the preset (becomes the widget title) */
  title: string
  /** Short description of what question it answers */
  description: string
  /** Category for grouping in the picker */
  category: string
  /** The measure key stored on the DashboardWidget (for goals linkage) */
  measure: string
  /** Full widget config */
  config: Partial<GenericWidgetConfig>
  /** Default grid size */
  gridW?: number
  gridH?: number
}

export const WIDGET_PRESETS: WidgetPreset[] = [
  // ── Speed & Efficiency ─────────────────────────────────────────
  // Note: there is no scalar "average days to award" metric on
  // performance_fact. Per FPDS, period_of_performance_start_date is usually
  // equal to date_signed, so any diff between them is meaningless. Use the
  // dedicated speed_to_contract mart, which measures solicitation→signing
  // via SAM Opportunities matching.

  {
    id: 'speed_to_contract_percentiles',
    title: 'Speed to Contract (Percentiles)',
    description: 'P25/P50/P75/P90 days from solicitation posting to award',
    category: 'Speed & Efficiency',
    measure: 'speed_to_contract',
    // chart_type is informational here; MeasureWidget routes 'speed_to_contract'
    // to the bespoke section component (Recharts → ECharts boxplot in Phase B3).
    config: { chart_type: 'boxplot' },
    gridW: 6,
    gridH: 5,
  },
  {
    id: 'fy_end_surge',
    title: 'Fiscal Year-End Surge',
    description: 'Monthly spend distribution — Q4 concentration detection',
    category: 'Speed & Efficiency',
    measure: 'fy_end_surge',
    // chart_type informational; routed to bespoke section (Recharts → ECharts calendar in Phase B3).
    config: { chart_type: 'calendar' },
  },
  {
    id: 'contract_duration',
    title: 'Contract Duration',
    description: 'POP duration percentiles by vehicle/pricing type',
    category: 'Speed & Efficiency',
    measure: 'contract_duration',
    // chart_type informational; routed to bespoke section (Recharts → ECharts boxplot in Phase B3).
    config: { chart_type: 'boxplot' },
  },

  // ── Competition ────────────────────────────────────────────────

  {
    id: 'competition_trend',
    title: 'Competition Rate (Trend)',
    description: 'Full & open vs not competed over time',
    category: 'Competition',
    measure: 'competition_health',
    config: {
      group_by: 'fiscal_year',
      metrics: ['full_open_pct', 'not_competed_pct'],
      chart_type: 'line',
    },
  },
  {
    // Phase K1 — the honest companion to Full & Open. You held a competition,
    // but only one bidder showed up. Direct expert-critique add.
    id: 'single_bid_trend',
    title: 'Single-Bid Rate (Trend)',
    description: 'Share of competed awards that received only one bid — uncontested competition',
    category: 'Competition',
    measure: 'competition_health',
    config: {
      group_by: 'fiscal_year',
      metrics: ['single_offer_pct'],
      chart_type: 'line',
    },
  },
  {
    // Phase K1 — single-bid by MAJCOM (ranked).
    id: 'single_bid_by_macom',
    title: 'Single-Bid by MAJCOM',
    description: 'Which commands have the most uncontested competitions?',
    category: 'Competition',
    measure: 'competition_health',
    config: {
      group_by: 'macom',
      metrics: ['single_offer_pct'],
      chart_type: 'bar',
      sort_by: '-single_offer_pct',
      limit: 15,
    },
  },
  {
    // Phase K1 — follow-on share. Incumbent retention indicator.
    id: 'follow_on_trend',
    title: 'Follow-On Share (Trend)',
    description: 'Share of awards that follow a prior contract — incumbent retention',
    category: 'Competition',
    measure: 'competition_health',
    config: {
      group_by: 'fiscal_year',
      metrics: ['follow_on_pct'],
      chart_type: 'line',
    },
  },
  {
    // Phase K2 — modification rate (visual). Per-action award_count vs
    // distinct unique_award_count; the gap = modifications. Spiking mods
    // is a leading indicator of scope churn / requirements drift.
    id: 'modification_rate',
    title: 'Modification Volume (Actions vs Distinct Contracts)',
    description: 'Award actions vs distinct contracts — the gap is modifications',
    category: 'Competition',
    measure: 'office_performance',
    config: {
      group_by: 'fiscal_year',
      metrics: ['award_count', 'unique_award_count'],
      chart_type: 'line',
    },
  },
  {
    // Phase K3 — deobligation rate. Uses backend M.M7's positive/negative
    // dollar decomposition. Spiking deobs = bad initial estimates / scope
    // unrealized.
    id: 'deobligation_trend',
    title: 'Deobligation Rate (Trend)',
    description: 'Share of obligated dollars taken back via negative actions',
    category: 'Spend & Activity',
    measure: 'office_performance',
    config: {
      group_by: 'fiscal_year',
      metrics: ['deobligation_pct'],
      chart_type: 'line',
    },
  },
  {
    id: 'deobligation_by_macom',
    title: 'Deobligation Rate by MAJCOM',
    description: 'Where are we taking dollars back the most?',
    category: 'Spend & Activity',
    measure: 'office_performance',
    config: {
      group_by: 'macom',
      metrics: ['deobligation_pct'],
      chart_type: 'bar',
      sort_by: '-deobligation_pct',
      limit: 15,
    },
  },
  {
    id: 'competition_by_macom',
    title: 'Competition by MAJCOM',
    description: 'Which commands compete the most?',
    category: 'Competition',
    measure: 'competition_health',
    config: {
      group_by: 'macom',
      metrics: ['full_open_pct'],
      chart_type: 'bar',
      sort_by: '-award_count',
      limit: 20,
    },
  },
  {
    id: 'competition_heatmap',
    title: 'Competition Heatmap',
    description: 'Competition rate by MAJCOM over time',
    category: 'Competition',
    measure: 'competition_health',
    config: {
      group_by: 'fiscal_year',
      series_by: 'macom',
      metrics: ['full_open_pct'],
      chart_type: 'heatmap',
    },
    gridW: 12,
    gridH: 7,
  },
  {
    id: 'vendor_concentration',
    title: 'Vendor Concentration (Pareto)',
    description: 'Top 20 vendors by dollar share with cumulative-share Pareto line',
    category: 'Competition',
    measure: 'vendor_concentration',
    // Bare-bones Pareto preset wired to the dedicated /api/govdata/vendor-concentration/
    // endpoint via the legacy VendorConcentrationSection (Recharts → ECharts in B3).
    // Issue #90 §B fix: empty config used to fall through to office_performance defaults
    // (group_by=fiscal_year), showing FY rows instead of vendors.
    config: {
      group_by: 'fiscal_year',
      metrics: ['total_obligated'],
      chart_type: 'bar',
      sort_by: '-total_obligated',
      limit: 20,
    },
    gridW: 12,
    gridH: 6,
  },

  // ── Small Business ─────────────────────────────────────────────

  {
    id: 'sb_trend',
    title: 'Small Business (Trend)',
    description: '8(a), HUBZone, SDVOSB, WOSB awards over time',
    category: 'Small Business',
    measure: 'small_biz_engagement',
    config: {
      group_by: 'fiscal_year',
      metrics: ['count_8a', 'count_hubzone', 'count_sdvosb', 'count_wosb'],
      // Lines, not bars: each program (8(a) / HUBZone / SDVOSB / WOSB)
      // moves independently — separate lines remove the legend hunt that
      // a 4-metric grouped-bar imposed.
      chart_type: 'line',
    },
  },
  {
    id: 'sb_by_agency',
    title: 'Small Business by Agency',
    description: 'Which agencies engage small businesses most?',
    category: 'Small Business',
    measure: 'small_biz_engagement',
    config: {
      group_by: 'sub_agency',
      metrics: ['set_aside_pct'],
      chart_type: 'bar',
    },
  },

  // ── Vehicles & Pathways ────────────────────────────────────────

  {
    id: 'vehicle_mix',
    title: 'Vehicle Mix (Trend)',
    description: 'GWAC, IDIQ, BPA, OT usage over time',
    category: 'Vehicles & Pathways',
    measure: 'contract_type_trends',
    config: {
      // FY on the x-axis with one line per vehicle reads as a trend; the
      // previous (group_by=vehicle, series_by=fy) shape forced FY into a
      // grouped-bar legend hunt. Same data, more honest visualization.
      group_by: 'fiscal_year',
      series_by: 'vehicle_category',
      metrics: ['award_count'],
      chart_type: 'line',
    },
  },
  {
    id: 'far_vs_ot',
    title: 'FAR vs OT (Trend)',
    description: 'Other Transaction adoption over time',
    category: 'Vehicles & Pathways',
    measure: 'ot_prevalence',
    config: {
      group_by: 'fiscal_year',
      metrics: ['award_count'],
      series_by: 'is_ot',
      chart_type: 'bar',
    },
  },
  {
    // Raw OT volume (the "Number of OTs" half of the ask) as a first-class
    // preset — distinct from far_vs_ot's two-series count and
    // ot_prevalence_trend's rate. is_ot filters to OT rows; unique_award_count
    // counts distinct contracts (mods excluded). Mirrors the seeded
    // "OT Award Volume by FY" widget in seed_ota_dashboard.
    id: 'ot_award_volume_trend',
    title: 'OT Award Volume (Trend)',
    description: 'Number of Other Transaction awards over time',
    category: 'Vehicles & Pathways',
    measure: 'ot_prevalence',
    config: {
      group_by: 'fiscal_year',
      metrics: ['unique_award_count'],
      is_ot: true,
      chart_type: 'line',
    },
  },
  {
    // Phase M.M1 — uses the now-working ot_award_pct rate column.
    id: 'ot_prevalence_trend',
    title: 'OT Adoption Rate (Trend)',
    description: 'Share of awards using Other Transactions over time',
    category: 'Vehicles & Pathways',
    measure: 'ot_prevalence',
    config: {
      group_by: 'fiscal_year',
      metrics: ['ot_award_pct'],
      chart_type: 'line',
    },
  },
  {
    id: 'ot_by_macom',
    title: 'OT Adoption by MAJCOM',
    description: 'Which commands use Other Transactions?',
    category: 'Vehicles & Pathways',
    measure: 'ot_prevalence',
    config: {
      group_by: 'macom',
      metrics: ['award_count'],
      series_by: 'is_ot',
      chart_type: 'bar',
      limit: 15,
    },
  },

  // ── Spend & Activity ───────────────────────────────────────────

  {
    id: 'spend_by_vertical',
    title: 'Spend by Vertical',
    description: 'Where is money going by product/service category?',
    category: 'Spend & Activity',
    measure: 'activity_by_vertical',
    config: {
      group_by: 'psc_level1_code',
      metrics: ['total_obligated'],
      chart_type: 'bar',
      sort_by: '-total_obligated',
    },
  },
  {
    // Phase C2 — Treemap variant of Spend by Vertical. With ≥10 PSC L1
    // categories on a single chart, leaf area encodes magnitude better than
    // a bar's length, and the layout uses 2D space efficiently.
    id: 'spend_by_vertical_treemap',
    title: 'Spend by Vertical (Treemap)',
    description: 'PSC L1 spend as area-encoded leaves — magnitudes at a glance',
    category: 'Spend & Activity',
    measure: 'activity_by_vertical',
    config: {
      group_by: 'psc_level1_code',
      metrics: ['total_obligated'],
      chart_type: 'treemap',
      sort_by: '-total_obligated',
    },
    gridW: 8,
    gridH: 6,
  },
  {
    // Phase C2 — MAJCOM treemap. Sub-agency / MAJCOM dollar share where the
    // branch-of-service relative magnitudes matter (Air Force vs Army vs
    // Navy + components).
    id: 'spend_by_macom_treemap',
    title: 'Spend by MAJCOM (Treemap)',
    description: 'MAJCOM dollar share as a treemap — relative footprint within the portfolio',
    category: 'Spend & Activity',
    measure: 'office_performance',
    config: {
      group_by: 'macom',
      metrics: ['total_obligated'],
      chart_type: 'treemap',
      sort_by: '-total_obligated',
      limit: 30,
    },
    gridW: 8,
    gridH: 6,
  },
  {
    id: 'spend_by_color',
    title: 'Spend by Color of Money',
    description: 'RDT&E vs Procurement vs O&M vs WCF',
    category: 'Spend & Activity',
    measure: 'office_performance',
    config: {
      group_by: 'color_of_money',
      metrics: ['total_obligated'],
      chart_type: 'bar',
      sort_by: '-total_obligated',
    },
  },
  {
    // Phase C7 — Sankey: Color of Money → Vehicle Category dollar flow.
    // Answers "what kinds of money flow through what kinds of vehicles?" — a
    // canonical PAE/SAE question. group_by = source dim, series_by = target
    // dim; renderer pivots flat rows into nodes + links.
    id: 'spend_flow_color_to_vehicle',
    title: 'Spend Flow: Color of Money → Vehicle',
    description: 'Sankey of obligated $ flow from money color into contract vehicle',
    category: 'Spend & Activity',
    measure: 'office_performance',
    config: {
      group_by: 'color_of_money',
      series_by: 'vehicle_category',
      metrics: ['total_obligated'],
      chart_type: 'sankey',
    },
    gridW: 12,
    gridH: 6,
  },
  {
    id: 'awards_overview_table',
    title: 'Awards Overview (Table)',
    description: 'Award counts and dollars by agency — sortable scorecard',
    category: 'Spend & Activity',
    measure: 'acq_performance',
    config: {
      group_by: 'sub_agency',
      metrics: ['award_count', 'total_obligated', 'full_open_pct'],
      chart_type: 'table',
    },
    gridW: 12,
    gridH: 6,
  },

  // ── Ranked Offices (Phase H2 expert-critique add) ─────────────
  // The single most-asked-each-morning leader question: "where am I
  // relative to my peers?" Each preset ranks offices/MAJCOMs/sub-agencies
  // on a chosen metric, sorted worst-to-best so the offices needing
  // attention are top-of-list.
  {
    id: 'ranked_macoms_full_open',
    title: 'MAJCOMs Ranked by Full & Open %',
    description: 'Worst-to-best on competition rate — focus the morning conversation',
    category: 'Ranked Offices',
    measure: 'competition_health',
    config: {
      group_by: 'macom',
      metrics: ['full_open_pct'],
      chart_type: 'bar',
      sort_by: 'full_open_pct',
      limit: 20,
    },
  },
  {
    id: 'ranked_macoms_sb',
    title: 'MAJCOMs Ranked by SB Award %',
    description: 'Worst-to-best on small-business engagement',
    category: 'Ranked Offices',
    measure: 'small_biz_engagement',
    config: {
      group_by: 'macom',
      metrics: ['sb_award_pct'],
      chart_type: 'bar',
      sort_by: 'sb_award_pct',
      limit: 20,
    },
  },
  {
    id: 'ranked_subagencies_obligated',
    title: 'Sub-Agencies Ranked by Obligated $',
    description: 'Top spenders across DoW sub-agencies',
    category: 'Ranked Offices',
    measure: 'office_performance',
    config: {
      group_by: 'sub_agency',
      metrics: ['total_obligated'],
      chart_type: 'bar',
      sort_by: '-total_obligated',
      limit: 25,
    },
  },
  {
    id: 'ranked_macoms_single_bid',
    title: 'MAJCOMs Ranked by Single-Bid %',
    description: 'Highest uncontested-competition rates first — attention-getter',
    category: 'Ranked Offices',
    measure: 'competition_health',
    config: {
      group_by: 'macom',
      metrics: ['single_offer_pct'],
      chart_type: 'bar',
      sort_by: '-single_offer_pct',
      limit: 20,
    },
  },
  {
    // Phase C5 / H4 — Slope chart of rank change between two FYs on F&O%.
    // Rank mode (1 = best) auto-spreads the lines vertically — way more
    // readable than value mode when most MAJCOMs cluster at ~95% F&O.
    // Each line's slope shows whether the MAJCOM moved up or down the
    // ranking; absolute % is in the tooltip.
    id: 'rank_change_full_open',
    title: 'F&O % Rank-Change Between FYs',
    description: 'Slope chart: which MAJCOMs moved up or down the F&O ranking?',
    category: 'Ranked Offices',
    measure: 'competition_health',
    config: {
      group_by: 'macom',
      series_by: 'fiscal_year',
      metrics: ['full_open_pct'],
      chart_type: 'slope',
      as_rank: true,
    },
    gridW: 8,
    gridH: 5,
  },
  {
    id: 'rank_change_sb',
    title: 'SB Award % Rank-Change Between FYs',
    description: 'Slope chart: small-business engagement movement by MAJCOM',
    category: 'Ranked Offices',
    measure: 'small_biz_engagement',
    config: {
      group_by: 'macom',
      series_by: 'fiscal_year',
      metrics: ['sb_award_pct'],
      chart_type: 'slope',
      as_rank: true,
    },
    gridW: 8,
    gridH: 5,
  },
  {
    // Companion to rank_change_full_open — same shape, single-bid metric.
    // Tracks the *uncontested-competition* movement so leaders can see
    // who's quietly losing competitive vendors even while their F&O%
    // stays high (the "competition theatre" pattern).
    id: 'rank_change_single_bid',
    title: 'Single-Bid % Rank-Change Between FYs',
    description: 'Slope chart: which MAJCOMs improved (or worsened) on uncontested competition?',
    category: 'Ranked Offices',
    measure: 'competition_health',
    config: {
      group_by: 'macom',
      series_by: 'fiscal_year',
      metrics: ['single_offer_pct'],
      chart_type: 'slope',
      as_rank: true,
    },
    gridW: 8,
    gridH: 5,
  },
  {
    id: 'rank_change_ot',
    title: 'OT Adoption % Rank-Change Between FYs',
    description: 'Slope chart: which MAJCOMs are leaning into Other Transactions vs falling off?',
    category: 'Ranked Offices',
    measure: 'ot_prevalence',
    config: {
      group_by: 'macom',
      series_by: 'fiscal_year',
      metrics: ['ot_award_pct'],
      chart_type: 'slope',
      as_rank: true,
    },
    gridW: 8,
    gridH: 5,
  },
  {
    id: 'rank_change_deobligation',
    title: 'Deobligation Rate Rank-Change Between FYs',
    description: 'Slope chart: which MAJCOMs got better at sizing awards vs worse (lower is better)?',
    category: 'Ranked Offices',
    measure: 'office_performance',
    config: {
      group_by: 'macom',
      series_by: 'fiscal_year',
      metrics: ['deobligation_pct'],
      chart_type: 'slope',
      as_rank: true,
    },
    gridW: 8,
    gridH: 5,
  },
  {
    // Phase C6 — Scatter / bubble: each point is one MAJCOM. X = Full & Open %,
    // Y = Single-Bid %. Healthy MAJCOMs cluster low-Y / high-X; outliers stand
    // out (high F&O *and* high single-bid is a contracting-officer competition
    // theatre rather than real competition). Bubble size = total awards.
    id: 'office_competition_scatter',
    title: 'Competition Health Scatter (F&O × Single-Bid)',
    description: 'Find MAJCOM outliers: high competition rate but uncontested in practice',
    category: 'Ranked Offices',
    measure: 'competition_health',
    config: {
      group_by: 'macom',
      metrics: ['full_open_pct', 'single_offer_pct'],
      size_metric: 'award_count',
      chart_type: 'scatter',
      limit: 30,
    },
    gridW: 8,
    gridH: 6,
  },
  {
    id: 'spend_trend',
    title: 'Obligated Dollars (Trend)',
    description: 'Total spend over time',
    category: 'Spend & Activity',
    measure: 'office_performance',
    config: {
      group_by: 'fiscal_year',
      metrics: ['total_obligated'],
      chart_type: 'line',
    },
  },
]

/** All presets including DIB / Geography presets — kept exported for any callers
 *  that genuinely want the merged set (e.g., search across both). */
export const ALL_PRESETS: WidgetPreset[] = [...WIDGET_PRESETS, ...DIB_WIDGET_PRESETS]

/**
 * Group presets by category, scoped per dashboard kind:
 * - 'leadership' (default) → only `WIDGET_PRESETS` (no DIB cross-listing on the
 *   main /exec/dashboard, which conflates DIB-vendor scope with leadership-portfolio scope).
 * - 'dib' → only `DIB_WIDGET_PRESETS` for /exec/dib/dashboard.
 * - 'all' → both sets, for the rare caller that wants the full merge.
 */
export function getPresetsByCategory(
  scope: 'leadership' | 'dib' | 'all' = 'leadership',
): Record<string, WidgetPreset[]> {
  const source =
    scope === 'leadership' ? WIDGET_PRESETS :
    scope === 'dib' ? DIB_WIDGET_PRESETS :
    ALL_PRESETS
  const groups: Record<string, WidgetPreset[]> = {}
  for (const preset of source) {
    if (!groups[preset.category]) groups[preset.category] = []
    groups[preset.category].push(preset)
  }
  return groups
}
